// Self-service credentials ("My credentials"). Read-only view of the
// authenticated user's own licenses, certifications, clearances, and firearms
// qualifications (self.read_credentials). Expiry dates are highlighted: red
// when within 30 days (or past), amber when within 90 days.

import type {
  Certification,
  Clearance,
  ClearanceLevel,
  FirearmsQualification,
  License,
  LicenseType,
  WeaponClass,
} from '@dsr/shared-types';
import { useMyCredentials } from '../api/queries/self';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { Badge, Table } from '../components/ui';
import type { BadgeTone } from '../components/ui';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Tone for an expiry date: danger if past/<=30d, warn if <=90d, else neutral. */
function expiryTone(epoch: number | null | undefined): BadgeTone {
  if (epoch == null) return 'neutral';
  const days = (epoch - Date.now()) / DAY_MS;
  if (days <= 30) return 'danger';
  if (days <= 90) return 'warn';
  return 'neutral';
}

function ExpiryCell({ epoch }: { epoch: number | null }): JSX.Element {
  return <Badge tone={expiryTone(epoch)}>{fmtDate(epoch)}</Badge>;
}

const LICENSE_TYPE_LABEL: Record<LicenseType, string> = {
  guard_card: 'Guard Card',
  armed_permit: 'Armed Permit',
  ccw: 'CCW',
  driver: 'Driver',
  medic_emr: 'Medic — EMR',
  medic_emt: 'Medic — EMT',
  private_investigator: 'Private Investigator',
  security_company: 'Security Company',
  other: 'Other',
};

const WEAPON_CLASS_LABEL: Record<WeaponClass, string> = {
  handgun: 'Handgun',
  rifle: 'Rifle',
  shotgun: 'Shotgun',
  less_lethal: 'Less-Lethal',
};

const CLEARANCE_LEVEL_LABEL: Record<ClearanceLevel, string> = {
  none: 'None',
  basic_bgc: 'Basic BGC',
  enhanced_bgc: 'Enhanced BGC',
  secret: 'Secret',
  top_secret: 'Top Secret',
};

export function MyCredentialsPage(): JSX.Element {
  const { data, isPending, isError, error } = useMyCredentials();

  if (isPending) {
    return <div className="page-state">Loading your credentials…</div>;
  }
  if (isError || !data) {
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <div className={`page-state${is404 ? '' : ' page-error'}`} role={is404 ? undefined : 'alert'}>
        {is404
          ? 'Your account is not linked to a staff record — no credentials on file.'
          : (error instanceof ApiError ? error.message : 'Failed to load your credentials.')}
      </div>
    );
  }

  return (
    <div className="page my-credentials-page">
      <header className="page-header">
        <div>
          <h1>My credentials</h1>
          <p className="page-sub">
            Your licenses, certifications, clearances, and firearms qualifications. Expiring items
            are highlighted.
          </p>
        </div>
      </header>

      {/* ---------- Licenses ---------- */}
      <section className="credential-section">
        <div className="section-header">
          <h2>Licenses</h2>
        </div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Number</Table.HeaderCell>
              <Table.HeaderCell>Authority / State</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {data.licenses.length === 0 && (
              <Table.Empty colSpan={5}>No licenses on file.</Table.Empty>
            )}
            {data.licenses.map((l: License) => (
              <Table.Row key={l.id}>
                <Table.Cell tone="strong">
                  {l.license_type ? LICENSE_TYPE_LABEL[l.license_type] : '—'}
                </Table.Cell>
                <Table.Cell tone="mono">{l.number ?? '—'}</Table.Cell>
                <Table.Cell>
                  {[l.issuing_authority, l.state].filter(Boolean).join(' · ') || '—'}
                </Table.Cell>
                <Table.Cell className="capitalize">{l.status}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={l.expires_at} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </section>

      {/* ---------- Certifications ---------- */}
      <section className="credential-section">
        <div className="section-header">
          <h2>Certifications</h2>
        </div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Issuer</Table.HeaderCell>
              <Table.HeaderCell>Issued</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {data.certifications.length === 0 && (
              <Table.Empty colSpan={4}>No certifications on file.</Table.Empty>
            )}
            {data.certifications.map((c: Certification) => (
              <Table.Row key={c.id}>
                <Table.Cell tone="strong">{c.name}</Table.Cell>
                <Table.Cell>{c.issuer ?? '—'}</Table.Cell>
                <Table.Cell>{fmtDate(c.issued_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={c.expires_at} />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </section>

      {/* ---------- Clearances ---------- */}
      <section className="credential-section">
        <div className="section-header">
          <h2>Clearances</h2>
        </div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Level</Table.HeaderCell>
              <Table.HeaderCell>Granted</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Adjudicator</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {data.clearances.length === 0 && (
              <Table.Empty colSpan={4}>No clearances on file.</Table.Empty>
            )}
            {data.clearances.map((c: Clearance) => (
              <Table.Row key={c.id}>
                <Table.Cell tone="strong">{CLEARANCE_LEVEL_LABEL[c.clearance_level]}</Table.Cell>
                <Table.Cell>{fmtDate(c.granted_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={c.expires_at} />
                </Table.Cell>
                <Table.Cell>{c.adjudicator ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </section>

      {/* ---------- Firearms qualifications ---------- */}
      <section className="credential-section">
        <div className="section-header">
          <h2>Firearms qualifications</h2>
        </div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Weapon class</Table.HeaderCell>
              <Table.HeaderCell>Score</Table.HeaderCell>
              <Table.HeaderCell>Qualified</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Instructor</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {data.firearms_qualifications.length === 0 && (
              <Table.Empty colSpan={5}>No firearms qualifications on file.</Table.Empty>
            )}
            {data.firearms_qualifications.map((f: FirearmsQualification) => (
              <Table.Row key={f.id}>
                <Table.Cell tone="strong">
                  {f.weapon_class ? WEAPON_CLASS_LABEL[f.weapon_class] : '—'}
                </Table.Cell>
                <Table.Cell>{f.score == null ? '—' : f.score}</Table.Cell>
                <Table.Cell>{fmtDate(f.qualified_at)}</Table.Cell>
                <Table.Cell>
                  <ExpiryCell epoch={f.expires_at} />
                </Table.Cell>
                <Table.Cell>{f.instructor ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </section>
    </div>
  );
}
